import { useSearchParamsMutation } from "logic/hooks";
import { fromInput, scaleDown, toUnits } from "logic/utils";
import { createContext, ReactNode, useContext, useState } from "react";

import { Loan } from "../../data/types";
import { useUILoanStatus } from "../../LoanStatusTag";
import { isQuoteQuarantined } from "../../quarantinedQuotes";
import { useLoanTimestamps } from "../../useLoanTimestamp";
import { useRepayInfo } from "../../useRepayInfo";

type Params = {
  loan: Loan;
  resetState: () => void;
};

function useLoanPaymentState(params: Params) {
  const { loan, resetState } = params;

  const loanTimestamps = useLoanTimestamps(loan);
  const uiLoanStatus = useUILoanStatus(loan);

  const isQuoteUnavailable = isQuoteQuarantined(loan);

  const { data } = useRepayInfo(loan, loanTimestamps.isRepayment);

  /**
   * Three-state quote, consumed as a `MetricTy` via `quote && <Node />`:
   * `null` when the quote is unavailable (renders `--`), `undefined` while it
   * loads (renders a skeleton), otherwise the resolved amounts. Propagate it
   * with `&&` rather than `?.` or `??`, which collapse the two empty states.
   */
  const quote = isQuoteUnavailable ? null : data;

  const sp = useSearchParamsMutation();
  const isPrepayment = sp.get("action") == "prepayment";
  function setIsPrepayment(value: boolean) {
    sp.set("action", value ? "prepayment" : undefined);
  }

  const [customPaymentAmount, setCustomPaymentAmount] = useState("");
  const customPaymentAmountUnits = toUnits(fromInput(customPaymentAmount), loan.erc20.decimals);

  // Fall back to 0 for the amount validation below; when the quote is
  // unavailable the payment button is disabled outright, so these bounds are
  // never the thing gating a submission.
  const quoteTotal = quote?.total ?? 0n;
  const quoteInterest = quote?.interest ?? 0n;

  const remainingBalance = scaleDown(loan.loanState.scaledBalance, loan.erc20.decimals);
  const maxToRepay = quoteTotal > remainingBalance ? quoteTotal : remainingBalance + quoteInterest;

  const customPaymentAmountError = !customPaymentAmount
    ? undefined
    : !loanTimestamps.isRepayment
      ? customPaymentAmountUnits <= 0n
        ? "Must be greater than 0"
        : customPaymentAmountUnits > maxToRepay
          ? "Custom payment amount must be less than the remaining principal"
          : undefined
      : customPaymentAmountUnits < quoteTotal
        ? "Custom payment amount must be greater than the total amount due"
        : customPaymentAmountUnits > maxToRepay
          ? "Custom payment amount must be less than the remaining principal"
          : undefined;

  return {
    loan,
    loanTimestamps,
    uiLoanStatus,
    quote,
    isQuoteUnavailable,
    isPrepayment,
    setIsPrepayment,
    customPaymentAmount,
    setCustomPaymentAmount,
    customPaymentAmountUnits,
    customPaymentAmountError,
    maxToRepay,
    resetState,
  };
}

const LoanPaymentContext = createContext<ReturnType<typeof useLoanPaymentState> | undefined>(undefined);

export function useLoanPayment() {
  const context = useContext(LoanPaymentContext);
  if (!context) throw new Error("useLoanPayment must be used within a LoanPaymentProvider");
  return context;
}

type Props = Params & {
  children: ReactNode;
};

export function LoanPaymentProvider(props: Props) {
  const { children, ...params } = props;

  const state = useLoanPaymentState(params);

  return <LoanPaymentContext.Provider value={state}>{children}</LoanPaymentContext.Provider>;
}
